import { ROUTES } from "@app/constants";
import { useGetBrands } from "@entities/brand";
import { PLATFORM_OPTIONS } from "@shared/lib/constants";
import { ToastService } from "@shared/lib/services";
import {
  IBrief,
  IBriefCreateRequest,
  IBriefUpdateRequest,
} from "@shared/lib/types/brief";
import { Button } from "@shared/ui/Button";
import { Form, Input, Select } from "antd";
import { format } from "date-fns";
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateBrief } from "../lib/hooks/useCreateBrief";
import { useUpdateBrief } from "../lib/hooks/useUpdateBrief";
import styles from "./styles.module.scss";
const { Option } = Select;

const BriefForm = ({
  closeModal,
  initialValues,
}: {
  closeModal: () => void;
  initialValues?: Partial<IBrief>;
}) => {
  const [form] = Form.useForm();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { brands, isFetching } = useGetBrands(10, 1, searchQuery);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const { create, isCreated, isCreating, newBriefData } = useCreateBrief();
  const { updateBrief, isUpdated, isUpdating } = useUpdateBrief();
  const handleCreate = (data: IBriefCreateRequest) => {
    create(data);
  };
  const handleUpdate = (data: IBriefUpdateRequest) => {
    updateBrief({
      updatedBrief: { ...data, briefId: initialValues?._id! },
    });
  };

  const navigate = useNavigate();
  useEffect(() => {
    if (isCreated || isUpdated) {
      if (isCreated) {
        navigate(
          ROUTES.BRIEF_V2_OVERVIEW.replace(
            ":briefId",
            newBriefData?.data?.briefId || ""
          )
        );
      }
      closeModal();
    }
  }, [isCreated, isUpdated]);

  const brandId = Form.useWatch("brandId", form);

  useEffect(() => {
    if (brandId) {
      const brandName =
        brands?.data.find((brand) => brand.brandId === brandId)?.name || "";

      const date = format(new Date(), "MMM do");
      const cName = `${brandName} ${date} campaign`;
      if (!initialValues?.name) {
        form.setFieldsValue({ name: cName });
      }
    }
  }, [brandId, form, initialValues]);

  return (
    <Form
      form={form}
      requiredMark={false}
      layout="vertical"
      onFinish={handleCreate}
      initialValues={{
        name: initialValues?.name || "",
        requirements: initialValues?.requirements || "",
        platform: initialValues?.form?.platform || [],
        brandId: initialValues?.brandId || undefined,
      }}
      onFinishFailed={({ errorFields }) => {
        if (errorFields.length) {
          ToastService.error(
            "Error Submitting Form. Please fill all required fields."
          );
        }
      }}
    >
      <div className={styles.form}>
        {/* <Form.Item>
          <Space>
            <Avatar
              src={brandMe?.brand?.logoUrl}
              shape="square"
              size={64}
              className={styles.brandLogo}
            />
            <div className={styles.brandInfo}>
              <div className={styles.brandNameLabel}>Brand Name</div>
              <div className={styles.brandNameValue}>
                {brandMe?.brand?.name || "Brand"}
              </div>
            </div>
          </Space>
        </Form.Item> */}
        <Form.Item name="brandId" label="Brand" className={styles.formField}>
          <Select
            placeholder="Select Brand"
            showSearch
            filterOption={false}
            onSearch={setSearchQuery}
            loading={isFetching}
            options={brands?.data?.map((val) => ({
              key: val._id,
              label: val.name,
              value: val._id,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="name"
          label="Campaign Name"
          rules={[{ required: true, message: "Campaign Name is required" }]}
          className={styles.formField}
        >
          <Input placeholder="e.g. Summer Collection Launch 2024" />
        </Form.Item>
        <Form.Item
          name="platform"
          label="Platforms"
          rules={[
            {
              required: true,
              type: "array",
              min: 1,
              message: "Please select at least one platform",
            },
          ]}
          className={styles.formField}
        >
          <Select
            mode="multiple"
            placeholder="Select platforms"
            options={[...PLATFORM_OPTIONS]}
            allowClear
            size="middle"
          />
        </Form.Item>
      </div>
      <div className={styles.footer}>
        <Form.Item noStyle>
          <Button
            type="text"
            htmlType="button"
            disabled={isCreating}
            onClick={closeModal}
          >
            Cancel
          </Button>
        </Form.Item>
        <Form.Item noStyle>
          <Button
            type="primary"
            htmlType="submit"
            disabled={isCreating}
            loading={isCreating}
            icon={<ArrowRight size={16} />}
            iconPosition="end"
            style={{ height: 35 }}
          >
            {isCreating ? "Creating..." : "Create Campaign"}
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default BriefForm;
